/**
 * The picture operations (spec 026): noting, removing and filling the
 * pictures of a story's picture map. Pure and replayable like every story
 * operation (023); they are appended after the wrapped scene operations.
 */
import type { CharacterFraming, PictureKind, PictureMap, PictureSlot, SlotUse, Sprite, SpritePictureKind, StoredPictureSlot } from '../@types';
import { validateStatePlayback } from '../utils/spriteSheet';
import { PLAYBACK_ARG_SCHEMA, playbackFromArg, type PlaybackArg } from '../operations/playbackArg';
import {
  CHARACTER_FRAMINGS, DEFAULT_BACKGROUND_NAME, FRAMING_LABELS, isCharacterFraming, isPictureKind, isSpritePictureKind, masterSlotOf,
  PICTURE_KINDS, pictureKindOf, pictureKindPhrase, pictureSlotId, pictureSlotLabel, pictureSlug, SPRITE_PICTURE_KINDS, storedSlotOf, syncPictureMap,
} from '../pictures/pictureMap';
import { sizeFromImage } from '../utils/spriteSize';
import { backgroundStates, hasBackgroundStates, writeBackgroundStates } from '../utils/backgroundStates';
import type { JsonSchema, OperationContext } from '../operations/types';
import type { StoryDocScene, StoryDocument, StoryOperation, StoryOperationError, StoryOperationResult } from './types';

const str = (description: string): JsonSchema => ({ type: 'string', description });
const int = (description: string): JsonSchema => ({ type: 'integer', description });
const num = (description: string): JsonSchema => ({ type: 'number', description });
const obj = (properties: Record<string, JsonSchema>, required: string[] = []): JsonSchema => ({
  type: 'object', properties, required, additionalProperties: false,
});

const e = (code: StoryOperationError['code'], message: string, path?: string): StoryOperationError =>
  (path ? { code, message, path } : { code, message });
const fail = (...errors: StoryOperationError[]): StoryOperationResult => ({ ok: false, errors });

/** Lengths after trimming (spec 026). */
export const PICTURE_LIMITS = Object.freeze({ subject: 120, variant: 60, brief: 500, why: 200 });

/** The owner word of the messages (spec 030): `'story'` unless the context says `'scene'`. */
const nounOf = (ctx: OperationContext | undefined): 'story' | 'scene' => (ctx?.pictureNoun === 'scene' ? 'scene' : 'story');

const SLOT_ID_SCHEMA = str('A picture id from the story summary\'s Pictures section, e.g. character:priya:frustrated.');

// ─── 1. notePictureSlot ─────────────────────────────────────────────────────

export interface NotePictureSlotArgs {
  kind: PictureKind;
  subject: string;
  variant?: string;
  brief: string;
  why: string;
}

const MAX_LISTED = 20;
const distinct = (names: readonly string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of names) {
    const key = n.trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
};
const listed = (names: readonly string[]): string => {
  const list = distinct(names).slice(0, MAX_LISTED);
  return list.length ? list.join(', ') : 'none yet';
};

/**
 * D-13: a noted picture links to the story as it stands at this step. A
 * character, prop or other subject names a sprite of some scene and the
 * variant one of its states; a background subject names a named background.
 * A character's messages say "character" and "expression", a prop's and a
 * full picture's "sprite" and "state" (D-7).
 */
function linkError(doc: StoryDocument, kind: PictureKind, subject: string, variant: string, noun: 'story' | 'scene'): StoryOperationError | undefined {
  const subjectSlug = pictureSlug(subject);
  if (kind === 'background') {
    const names = doc.scenes
      .map((s) => (s.doc.background?.name ?? '').trim())
      .filter((n) => n && n !== DEFAULT_BACKGROUND_NAME);
    const named = doc.scenes.map((s) => s.doc.background).filter((bg) => !!bg && pictureSlug(bg.name) === subjectSlug);
    if (named.length === 0) {
      return e('not_found', `No background named "${subject}" in this ${noun}. Named backgrounds: ${listed(names)}. Name the scene's background with setBackground {name} first.`, 'subject');
    }
    // Spec 039 D-6: a background with states is noted per state, one with none without a variant.
    const states = named.flatMap((bg) => backgroundStates(bg).map((st) => st.name));
    const variantSlug = pictureSlug(variant);
    if (!variantSlug) {
      if (named.some((bg) => !hasBackgroundStates(bg))) return undefined;
      return e('invalid_args', `Background "${named[0]!.name}" has states: ${listed(states)}. Note one of them as the variant.`, 'variant');
    }
    if (states.length === 0) {
      return e('invalid_args', `Background "${named[0]!.name}" has no states, so its picture takes no variant. Give it states with setBackgroundStates first.`, 'variant');
    }
    if (states.some((n) => pictureSlug(n) === variantSlug)) return undefined;
    return e('not_found', `Background "${named[0]!.name}" has no state "${variant}". States: ${listed(states)}. Add it with setBackgroundStates first.`, 'variant');
  }
  const named = subjectSprites(doc, subject);
  if (named.length === 0) return noSpriteError(doc, kind, subject, noun);
  const variantSlug = pictureSlug(variant);
  if (named.some((sp) => (sp.states ?? []).some((st) => pictureSlug(st.name) === variantSlug))) return undefined;
  const states = named.flatMap((sp) => (sp.states ?? []).map((st) => st.name));
  return kind === 'character'
    ? e('not_found', `${named[0].name} has no expression "${variant}". Expressions: ${listed(states)}. Add it with editSprite first.`, 'variant')
    : e('not_found', `${named[0].name} has no state "${variant}". States: ${listed(states)}. Add it with editSprite first.`, 'variant');
}

/** The sprites of every scene whose name slugs like the subject. */
const subjectSprites = (doc: StoryDocument, subject: string): Sprite[] => {
  const key = pictureSlug(subject);
  return doc.scenes.flatMap((s) => s.doc.sprites ?? []).filter((sp) => pictureSlug(sp.name) === key);
};

/** `not_found` at `subject` for a sprite subject no scene has (D-13): a character's words, or a prop's and a full picture's. */
function noSpriteError(doc: StoryDocument, kind: PictureKind, subject: string, noun: 'story' | 'scene'): StoryOperationError {
  const names = listed(doc.scenes.flatMap((s) => s.doc.sprites ?? []).map((sp) => sp.name));
  return kind === 'character'
    ? e('not_found', `No character named "${subject}" in this ${noun}. Characters: ${names}. Use the sprite's exact name, or add the character first.`, 'subject')
    : e('not_found', `No sprite named "${subject}" in this ${noun}. Sprites: ${names}. Use the sprite's exact name, or add the sprite first.`, 'subject');
}

/**
 * Re-keys every stored sprite slot of a subject to `kind` (D-26, D-28): the
 * kind and the id change at the same position; subject, variant, brief, why
 * and `mediaId` stay; leaving `character` drops the framing. Two slots that
 * end with one id merge into the first in stored order, which keeps its
 * brief, why and `mediaId`, or the other's where its own is empty.
 */
function rekeySubject(slots: readonly StoredPictureSlot[], subject: string, kind: SpritePictureKind): StoredPictureSlot[] {
  const key = pictureSlug(subject);
  const out: StoredPictureSlot[] = [];
  const at = new Map<string, number>();
  for (const sl of slots) {
    const mine = isSpritePictureKind(sl.kind) && pictureSlug(sl.subject) === key;
    if (!mine) { out.push(sl); continue; }
    const next = storedSlotOf({ ...sl, kind, id: pictureSlotId(kind, sl.subject, sl.variant) });
    const prior = at.get(next.id);
    if (prior === undefined) {
      at.set(next.id, out.length);
      out.push(next);
      continue;
    }
    const first = out[prior];
    out[prior] = storedSlotOf({
      ...first,
      brief: first.brief.trim() ? first.brief : next.brief,
      why: first.why.trim() ? first.why : next.why,
      mediaId: typeof first.mediaId === 'number' && first.mediaId > 0 ? first.mediaId : next.mediaId,
      framing: first.framing ?? next.framing,
    });
  }
  return out;
}

export const notePictureSlot: StoryOperation<NotePictureSlotArgs> = {
  name: 'notePictureSlot',
  description: 'Note a picture the story uses, filled or not, in its picture map, after the sprite or named background exists: who or what it shows (subject; for a sprite also the state as variant), a brief an artist could draw from, and why the story needs it. Noting the same picture again updates its brief and why; noting a sprite with another kind changes the kind of all its pictures.',
  inputSchema: obj({
    kind: { type: 'string', enum: PICTURE_KINDS, description: 'character: a person, cut out, with expressions; prop: an object, cut out, with states; background: the scene\'s own background (named with setBackground); other: a sprite showing a whole picture with its own background.' },
    subject: str(`Who or what it shows: the sprite's exact name, or the background's name (set with setBackground {name} first) (1–${PICTURE_LIMITS.subject} characters).`),
    variant: str(`The sprite's state: a character's expression or a prop's state, e.g. neutral or open (at most ${PICTURE_LIMITS.variant} characters); for a background only when it has states, the state's name.`),
    brief: str(`One or two sentences an artist could draw from (at most ${PICTURE_LIMITS.brief} characters).`),
    why: str(`One line: why the story needs it (at most ${PICTURE_LIMITS.why} characters).`),
  }, ['kind', 'subject', 'brief', 'why']),
  apply(doc, args, ctx) {
    const errors: StoryOperationError[] = [];
    const subject = args.subject.trim();
    const variant = (args.variant ?? '').trim();
    const brief = args.brief.trim();
    const why = args.why.trim();
    if (!isPictureKind(args.kind)) return fail(e('invalid_args', 'kind is character, prop, background or other', 'kind'));
    if (subject.length < 1 || subject.length > PICTURE_LIMITS.subject || !pictureSlug(subject)) {
      errors.push(e('out_of_range', `subject must be 1–${PICTURE_LIMITS.subject} characters with at least one letter or digit`, 'subject'));
    }
    if (args.kind !== 'background' && !pictureSlug(variant)) {
      errors.push(e('invalid_args', `a ${args.kind} picture needs a variant (its ${args.kind === 'character' ? 'expression' : 'state'})`, 'variant'));
    }
    if (variant.length > PICTURE_LIMITS.variant) errors.push(e('out_of_range', `variant must be at most ${PICTURE_LIMITS.variant} characters`, 'variant'));
    if (brief.length > PICTURE_LIMITS.brief) errors.push(e('out_of_range', `brief must be at most ${PICTURE_LIMITS.brief} characters`, 'brief'));
    if (why.length > PICTURE_LIMITS.why) errors.push(e('out_of_range', `why must be at most ${PICTURE_LIMITS.why} characters`, 'why'));
    if (errors.length) return fail(...errors);

    const unlinked = linkError(doc, args.kind, subject, variant, nounOf(ctx));
    if (unlinked) return fail(unlinked);

    // D-26: the latest note decides the subject's kind; its other stored slots are re-keyed first.
    let slots = doc.pictureMap.slots;
    let kindNote = '';
    if (isSpritePictureKind(args.kind)) {
      const key = pictureSlug(subject);
      const other = slots.some((sl) => isSpritePictureKind(sl.kind) && sl.kind !== args.kind && pictureSlug(sl.subject) === key);
      if (other) {
        slots = rekeySubject(slots, subject, args.kind);
        kindNote = `; ${subjectSprites(doc, subject)[0]?.name ?? subject} is now ${pictureKindPhrase(args.kind)}`;
      }
    }
    const id = pictureSlotId(args.kind, subject, variant);
    const at = slots.findIndex((s) => s.id === id);
    let next: StoredPictureSlot[];
    let label: string;
    if (at === -1) {
      const slot: StoredPictureSlot = { id, kind: args.kind, subject, variant, brief, why };
      next = [...slots, slot];
      label = pictureSlotLabel(slot);
    } else {
      next = slots.map((s, i) => (i === at ? { ...s, brief, why } : s));
      label = pictureSlotLabel(slots[at]);
    }
    return { ok: true, doc: { ...doc, pictureMap: { ...doc.pictureMap, slots: next } }, summary: `Picture noted: ${label}${kindNote}`, createdIds: [] };
  },
};

// ─── 2. removePictureSlot ───────────────────────────────────────────────────

export interface RemovePictureSlotArgs { slot: string }

export const removePictureSlot: StoryOperation<RemovePictureSlotArgs> = {
  name: 'removePictureSlot',
  description: 'Remove a noted picture from the picture map. Scene content is not touched, so a picture a scene still needs comes back without its brief.',
  inputSchema: obj({ slot: SLOT_ID_SCHEMA }, ['slot']),
  apply(doc, args) {
    const slot = doc.pictureMap.slots.find((s) => s.id === args.slot);
    if (!slot) return fail(e('not_found', `no noted picture ${args.slot}`, 'slot'));
    return {
      ok: true,
      doc: { ...doc, pictureMap: { slots: doc.pictureMap.slots.filter((s) => s !== slot) } },
      summary: `Picture removed: ${pictureSlotLabel(slot)}`,
      createdIds: [],
    };
  },
};

// ─── 3. fillPictureSlot ─────────────────────────────────────────────────────

export interface FillPictureSlotArgs {
  slot: string;
  mediaId: number;
  size?: { width: number; height: number };
  uses?: SlotUse[];
}

/** Two uses are the same place; a background use is told apart by its state (spec 039 D-6). The editor stores share it (spec 040). */
export const sameUse = (a: SlotUse, b: SlotUse) =>
  a.kind === b.kind && a.scene === b.scene
  && (a.kind === 'background' ? (a.state ?? '') === (b.state ?? '') : (a.spriteId === b.spriteId && a.state === b.state));

/**
 * A background with one state's picture set (`mediaId` above 0) or cleared
 * (spec 039), through the one writer so the default state's picture stays
 * `baseMediaId` (D-1). The same object when the state is missing.
 */
function withStatePicture<B extends StoryDocScene['doc']['background']>(bg: B, state: string, mediaId: number | undefined): B {
  const states = backgroundStates(bg);
  if (!states.some((s) => s.name === state)) return bg;
  const next = states.map((s) => {
    if (s.name !== state) return s;
    const { mediaId: _old, ...rest } = s;
    return mediaId !== undefined && mediaId > 0 ? { ...rest, mediaId } : rest;
  });
  const written = writeBackgroundStates(bg, next, bg.defaultState && states.some((s) => s.name === bg.defaultState) ? bg.defaultState : undefined);
  return written.ok ? (written.background as B) : bg;
}

const USE_SCHEMA = obj({
  kind: { type: 'string', enum: ['sprite', 'line', 'background'], description: 'sprite, line or background.' },
  scene: str('The scene key.'),
  spriteId: str('sprite, line: the sprite id.'),
  state: str('sprite, line: the state name.'),
  dialogId: str('line: the dialog id.'),
  lineId: str('line: the line id.'),
  line: int('line: the line number in the scene.'),
}, ['kind', 'scene']);

export const fillPictureSlot: StoryOperation<FillPictureSlotArgs> = {
  name: 'fillPictureSlot',
  description: 'Fill a picture of the map with a library image: it becomes that expression\'s or background\'s picture in every place it is used (or only in the listed uses).',
  inputSchema: obj({
    slot: SLOT_ID_SCHEMA,
    mediaId: int('The image media id.'),
    size: obj({ width: num('Natural width, px.'), height: num('Natural height, px.') }, ['width', 'height']),
    uses: { type: 'array', description: 'Only these uses of the picture; default every use.', items: USE_SCHEMA },
  }, ['slot', 'mediaId']),
  apply(doc, args, ctx) {
    const map = syncPictureMap(doc.pictureMap, doc.scenes);
    const found = map.slots.find((s) => s.id === args.slot);
    if (!found) return fail(e('not_found', `no picture ${args.slot} in the picture map`, 'slot'));
    if (ctx.media && !ctx.media.has(args.mediaId)) return fail(e('unknown_media', `media ${args.mediaId} is not in the library`, 'mediaId'));

    const stored = doc.pictureMap.slots;
    const at = stored.findIndex((s) => s.id === found.id);
    const base = at === -1 ? storedSlotOf({ ...found, brief: '', why: '', mediaId: undefined }) : stored[at];
    const filled: StoredPictureSlot = { ...base, mediaId: args.mediaId };
    const slots = at === -1 ? [...stored, filled] : stored.map((s, i) => (i === at ? filled : s));

    const targets = found.uses.filter((u) => u.kind !== 'line' && (!args.uses || args.uses.some((x) => sameUse(x, u))));
    let places = 0;
    const scenes = doc.scenes.map((scene): StoryDocScene => {
      const mine = targets.filter((u) => u.scene === scene.key);
      if (mine.length === 0) return scene;
      let sceneDoc = scene.doc;
      for (const use of mine) {
        if (use.kind === 'background') {
          sceneDoc = use.state !== undefined
            ? { ...sceneDoc, background: withStatePicture(sceneDoc.background, use.state, args.mediaId) }
            : { ...sceneDoc, background: { ...sceneDoc.background, baseMediaId: args.mediaId } };
          places += 1;
          continue;
        }
        const sprites = sceneDoc.sprites.map((sprite): Sprite => {
          if (sprite.id !== use.spriteId) return sprite;
          const next: Sprite = {
            ...sprite,
            states: sprite.states.map((st) => (st.name === use.state ? { ...st, mediaId: args.mediaId } : st)),
          };
          if (!sprite.size && args.size) next.size = sizeFromImage(args.size);
          return next;
        });
        sceneDoc = { ...sceneDoc, sprites };
        places += 1;
      }
      return { ...scene, doc: sceneDoc };
    });
    return {
      ok: true,
      doc: { ...doc, scenes, pictureMap: { slots } } as StoryDocument,
      summary: `Filled ${pictureSlotLabel(found)} in ${places} place${places === 1 ? '' : 's'}`,
      createdIds: [],
    };
  },
};

// ─── 4. setPictureFraming ───────────────────────────────────────────────────

export interface SetPictureFramingArgs { subject: string; framing: CharacterFraming }

/**
 * A character's framing (026 D-24, 027 D-22), for every expression at once:
 * set on each stored character slot of the subject, with the subject's
 * implicit slots stored first so they carry it. The Editor applies it; the
 * story assistant is not offered it (`STORY_AGENT_EXCLUDED`).
 */
export const setPictureFraming: StoryOperation<SetPictureFramingArgs> = {
  name: 'setPictureFraming',
  description: 'Set how a character is framed in generated pictures, for every expression of that character.',
  inputSchema: obj({
    subject: str('The sprite\'s exact name.'),
    framing: { type: 'string', enum: CHARACTER_FRAMINGS, description: 'head, half, knees or full.' },
  }, ['subject', 'framing']),
  apply(doc, args, ctx) {
    if (!isCharacterFraming(args.framing)) return fail(e('invalid_args', 'framing is head, half, knees or full', 'framing'));
    const subject = args.subject.trim();
    if (!pictureSlug(subject)) return fail(e('out_of_range', 'subject needs at least one letter or digit', 'subject'));
    const sprites = doc.scenes.flatMap((sc) => sc.doc.sprites ?? []);
    const key = pictureSlug(subject);
    const named = sprites.filter((sp) => pictureSlug(sp.name) === key);
    if (named.length === 0) {
      return fail(e('not_found', `No character named "${subject}" in this ${nounOf(ctx)}. Characters: ${listed(sprites.map((sp) => sp.name))}. Use the sprite's exact name, or add the character first.`, 'subject'));
    }
    const map = syncPictureMap(doc.pictureMap, doc.scenes);
    const kind = pictureKindOf(map, subject);
    if (kind !== 'character') {
      return fail(e('invalid_args', `${named[0].name} is ${pictureKindPhrase(kind)}; only a character has a framing`, 'subject'));
    }
    const stored = doc.pictureMap.slots;
    const storedIds = new Set(stored.map((sl) => sl.id));
    const added = map.slots
      .filter((sl) => sl.kind === 'character' && pictureSlug(sl.subject) === key && !storedIds.has(sl.id))
      .map((sl) => storedSlotOf({ ...sl, brief: '', why: '' }));
    const slots = [...stored, ...added].map((sl) =>
      (sl.kind === 'character' && pictureSlug(sl.subject) === key ? { ...sl, framing: args.framing } : sl));
    return {
      ok: true,
      doc: { ...doc, pictureMap: { ...doc.pictureMap, slots } },
      summary: `Framing of ${named[0].name}: ${FRAMING_LABELS[args.framing]}`,
      createdIds: [],
    };
  },
};

// ─── 5. clearPictures ───────────────────────────────────────────────────────

export interface ClearPicturesArgs { mediaIds: number[] }

/** At most this many pictures in one discard (027 D-30). */
export const CLEAR_PICTURES_MAX = 200;

/**
 * Discards pictures from the story (027 D-30): every sprite state showing
 * one of `mediaIds` goes back to no picture (`mediaId` 0) and every
 * background showing one loses its base image, so their slots are needed
 * again and play 026's placeholders; a sprite keeps its size, its states and
 * their names, and a stored slot whose `mediaId` is one of them forgets it.
 * The media stay in the library. Not offered to the story assistant
 * (`STORY_AGENT_EXCLUDED`, D-32).
 */
export const clearPictures: StoryOperation<ClearPicturesArgs> = {
  name: 'clearPictures',
  description: 'Take pictures out of the story: every expression and background that shows one of these images goes back to its placeholder. The images stay in the library.',
  inputSchema: obj({
    mediaIds: { type: 'array', description: `The image media ids to take out (1–${CLEAR_PICTURES_MAX}, distinct).`, items: int('An image media id.') },
  }, ['mediaIds']),
  apply(doc, args, ctx) {
    const ids = args.mediaIds;
    if (!Array.isArray(ids) || ids.length < 1 || ids.length > CLEAR_PICTURES_MAX
      || ids.some((id) => !Number.isInteger(id) || id <= 0) || new Set(ids).size !== ids.length) {
      return fail(e('invalid_args', `mediaIds must be 1–${CLEAR_PICTURES_MAX} distinct positive integers`, 'mediaIds'));
    }
    const drop = new Set(ids);
    const cleared = new Set<number>();
    const scenes = doc.scenes.map((scene): StoryDocScene => {
      let changed = false;
      const sprites = (scene.doc.sprites ?? []).map((sprite): Sprite => {
        if (!(sprite.states ?? []).some((st) => drop.has(st.mediaId))) return sprite;
        changed = true;
        return {
          ...sprite,
          states: sprite.states.map((st) => {
            if (!drop.has(st.mediaId)) return st;
            cleared.add(st.mediaId);
            return { ...st, mediaId: 0 };
          }),
        };
      });
      let background = scene.doc.background;
      const base = background?.baseMediaId;
      if (background && hasBackgroundStates(background)) {
        // Each state showing one of them loses its picture (spec 039), the default's through the writer.
        for (const st of backgroundStates(background)) {
          if (typeof st.mediaId !== 'number' || !drop.has(st.mediaId)) continue;
          cleared.add(st.mediaId);
          background = withStatePicture(background, st.name, undefined);
          changed = true;
        }
      } else if (background && typeof base === 'number' && drop.has(base)) {
        cleared.add(base);
        const { baseMediaId: _gone, ...rest } = background;
        background = rest as typeof background;
        changed = true;
      }
      return changed ? { ...scene, doc: { ...scene.doc, sprites, background } } : scene;
    });
    if (cleared.size === 0) {
      return fail(e('not_found', nounOf(ctx) === 'scene' ? 'this scene shows none of these pictures' : 'no scene of this story shows any of these pictures', 'mediaIds'));
    }
    const slots = doc.pictureMap.slots.map((sl) => {
      if (typeof sl.mediaId !== 'number' || !drop.has(sl.mediaId)) return sl;
      const { mediaId: _gone, ...rest } = sl;
      return rest;
    });
    return {
      ok: true,
      doc: { ...doc, scenes, pictureMap: { ...doc.pictureMap, slots } } as StoryDocument,
      summary: `Discarded ${cleared.size} picture${cleared.size === 1 ? '' : 's'}`,
      createdIds: [],
    };
  },
};

// ─── 6. resetPictureSlot ────────────────────────────────────────────────────

/** What a Reset covers (D-33): a character's every expression (its master's Reset), or this slot alone. */
export type ResetScope = 'character' | 'slot';

export interface ResetPictureSlotArgs {
  slot: string;
  /** A character slot: `character` (the default) resets every expression and the framing; `slot` only this one. */
  scope?: ResetScope;
  /** A character only: the framing set after the reset, in the same step (the master's framing change, D-33). */
  framing?: CharacterFraming;
}

const hasPictureId = (id: unknown): boolean => typeof id === 'number' && id > 0;

export { masterSlotOf } from '../pictures/pictureMap';

/** Whether a character has a picture shown or remembered in any expression (a framing change then resets it, D-33). */
export function hasCharacterPictures(map: PictureMap | null | undefined, subject: string): boolean {
  const key = pictureSlug(subject);
  return (map?.slots ?? []).some((s) => s.kind === 'character' && pictureSlug(s.subject) === key
    && (hasPictureId(s.mediaId) || s.uses.some((u) => hasPictureId(u.mediaId))));
}

/**
 * The slots a Reset of `slotId` returns to their initial state (027 D-33,
 * D-37): a character's or a prop's every slot of that subject and kind (each
 * expression or state, used or only noted), since they are drawn from its
 * master (D-35, D-37); with scope `slot`, a background or a full picture
 * (`other`), the slot alone. Empty when the slot is not in the map.
 */
export function resetTargetsOf(map: PictureMap | null | undefined, slotId: string, scope: ResetScope = 'character'): PictureSlot[] {
  const slots = map?.slots ?? [];
  const found = slots.find((s) => s.id === slotId);
  if (!found) return [];
  if ((found.kind !== 'character' && found.kind !== 'prop') || scope === 'slot') return [found];
  const key = pictureSlug(found.subject);
  return slots.filter((s) => s.kind === found.kind && pictureSlug(s.subject) === key);
}


/**
 * Whether a Reset of `slotId` would change anything: a picture shown or
 * remembered, or (a character-wide Reset) a framing. Briefs are kept.
 */
export function isResettable(map: PictureMap | null | undefined, slotId: string, scope: ResetScope = 'character'): boolean {
  const wide = scope === 'character';
  return resetTargetsOf(map, slotId, scope).some((s) =>
    hasPictureId(s.mediaId) || (wide && isCharacterFraming(s.framing)) || s.uses.some((u) => hasPictureId(u.mediaId)));
}

/**
 * Reset (027 D-33): returns a picture to its initial state, as if the
 * character or background had just been added, keeping the story's look.
 * With `scope: 'slot'` it acts on one expression alone and keeps the
 * character's framing. Otherwise, for a character it acts on every expression (`resetTargetsOf`): each
 * sprite state showing one of them goes back to no picture (`mediaId` 0, 026's
 * placeholder), a sprite left with no picture in any state loses its `size`
 * (the next picture sizes it again, 005's `sizeFromImage`), and the
 * subject's stored slots forget their remembered `mediaId` and framing but
 * keep their brief and why (the note an author or the assistant wrote), so
 * Generate runs again from the kept brief; a stored slot with neither, used
 * or not, is removed. A background loses its base image the same way. The
 * media stay in the library. Not offered to the story assistant.
 */
export const resetPictureSlot: StoryOperation<ResetPictureSlotArgs> = {
  name: 'resetPictureSlot',
  description: 'Reset a picture to its initial state, keeping its brief: for a character the picture of every expression and the framing (then the given framing), for a prop the picture of every state, or with scope slot that expression or state only; for a background or a full picture its picture. The images stay in the library.',
  inputSchema: obj({
    slot: SLOT_ID_SCHEMA,
    scope: { type: 'string', enum: ['character', 'slot'], description: 'A character or a prop: character (default) resets the whole subject (every expression and the framing, or every state); slot only this one.' },
    framing: { type: 'string', enum: CHARACTER_FRAMINGS, description: 'A character only: the framing to set after the reset.' },
  }, ['slot']),
  apply(doc, args, ctx) {
    const map = syncPictureMap(doc.pictureMap, doc.scenes);
    const scope: ResetScope = args.scope === 'slot' ? 'slot' : 'character';
    if (args.scope !== undefined && args.scope !== 'slot' && args.scope !== 'character') {
      return fail(e('invalid_args', 'scope is character or slot', 'scope'));
    }
    const targets = resetTargetsOf(map, args.slot, scope);
    if (targets.length === 0) return fail(e('not_found', `no picture ${args.slot} in the picture map`, 'slot'));
    const found = targets.find((t) => t.id === args.slot) ?? targets[0];
    // The whole subject is a character's or a prop's; a background or a full picture resets alone (D-37).
    if (args.scope === 'character' && found.kind !== 'character' && found.kind !== 'prop') {
      return fail(e('invalid_args', `scope character is for a character or a prop; ${pictureSlotLabel(found)} is ${pictureKindPhrase(found.kind)}`, 'scope'));
    }
    const ids = new Set(targets.map((t) => t.id));

    // sceneKey|spriteId → the state names to empty; sceneKey → background.
    const states = new Map<string, Set<string>>();
    const backgrounds = new Set<string>();
    // sceneKey → the background states to empty (spec 039).
    const backgroundStatesToClear = new Map<string, Set<string>>();
    for (const t of targets) {
      for (const u of t.uses) {
        if (u.kind === 'background' && u.state !== undefined) {
          if (!backgroundStatesToClear.has(u.scene)) backgroundStatesToClear.set(u.scene, new Set());
          backgroundStatesToClear.get(u.scene)!.add(u.state);
        } else if (u.kind === 'background') backgrounds.add(u.scene);
        else if (u.kind === 'sprite' && u.spriteId && u.state !== undefined) {
          const k = `${u.scene}|${u.spriteId}`;
          if (!states.has(k)) states.set(k, new Set());
          states.get(k)!.add(u.state);
        }
      }
    }
    let changed = false;
    let pictures = 0;
    const scenes = doc.scenes.map((scene): StoryDocScene => {
      let sceneChanged = false;
      const sprites = (scene.doc.sprites ?? []).map((sprite): Sprite => {
        const names = states.get(`${scene.key}|${sprite.id}`);
        if (!names) return sprite;
        let touched = false;
        const nextStates = sprite.states.map((st) => {
          if (!names.has(st.name) || !hasPictureId(st.mediaId)) return st;
          touched = true;
          pictures += 1;
          return { ...st, mediaId: 0 };
        });
        const bare = nextStates.every((st) => !hasPictureId(st.mediaId));
        if (!touched && !(bare && sprite.size)) return sprite;
        sceneChanged = true;
        const next: Sprite = { ...sprite, states: nextStates };
        if (bare) delete next.size;
        return next;
      });
      let background = scene.doc.background;
      if (backgrounds.has(scene.key) && background && hasPictureId(background.baseMediaId)) {
        const { baseMediaId: _gone, ...rest } = background;
        background = rest as typeof background;
        sceneChanged = true;
        pictures += 1;
      }
      for (const name of backgroundStatesToClear.get(scene.key) ?? []) {
        if (!background || !hasPictureId(backgroundStates(background).find((st) => st.name === name)?.mediaId)) continue;
        background = withStatePicture(background, name, undefined);
        sceneChanged = true;
        pictures += 1;
      }
      if (!sceneChanged) return scene;
      changed = true;
      return { ...scene, doc: { ...scene.doc, sprites, background } };
    });
    // Briefs and whys stay (owner, 2026-10-18); the remembered picture and the framing go.
    const slots: StoredPictureSlot[] = [];
    for (const sl of doc.pictureMap.slots) {
      if (!ids.has(sl.id)) { slots.push(sl); continue; }
      // One expression alone keeps the character's framing (it belongs to every expression).
      const kept: StoredPictureSlot = {
        id: sl.id, kind: sl.kind, subject: sl.subject, variant: sl.variant, brief: sl.brief, why: sl.why,
        ...(scope === 'slot' && sl.kind === 'character' && isCharacterFraming(sl.framing) ? { framing: sl.framing } : {}),
      };
      const keep = !!((sl.brief ?? '').trim() || (sl.why ?? '').trim() || kept.framing);
      if (keep) slots.push(kept);
      if (!keep || JSON.stringify(kept) !== JSON.stringify(sl)) changed = true;
    }
    if (args.framing !== undefined && (found.kind !== 'character' || scope === 'slot' || !isCharacterFraming(args.framing))) {
      return fail(e('invalid_args', 'framing is head, half, knees or full, for a character-wide reset only', 'framing'));
    }
    if (!changed && args.framing === undefined) return fail(e('not_found', `${pictureSlotLabel(found)} has nothing to reset`, 'slot'));
    const label = (found.kind === 'character' || found.kind === 'prop') && scope === 'character' ? found.subject : pictureSlotLabel(found);
    let out = { ...doc, scenes, pictureMap: { ...doc.pictureMap, slots } } as StoryDocument;
    let summary = `Reset ${label}: ${pictures} picture${pictures === 1 ? '' : 's'} back to placeholders`;
    if (args.framing !== undefined) {
      const framed = setPictureFraming.apply(out, { subject: found.subject, framing: args.framing }, ctx);
      if (!framed.ok) return framed;
      out = framed.doc;
      summary = `${summary}; ${framed.summary}`;
    }
    return { ok: true, doc: out, summary, createdIds: [] };
  },
};

// ─── 7. setPicturePlayback ──────────────────────────────────────────────────

export interface SetPicturePlaybackArgs { slotId: string; playback: PlaybackArg }

/** The sprite uses of a slot, once each (a line use is the same state). */
export function slotStateUses(slot: Pick<PictureSlot, 'uses'>): SlotUse[] {
  const seen = new Set<string>();
  return slot.uses.filter((u) => {
    if (u.kind !== 'sprite' || !u.spriteId || u.state === undefined) return false;
    const key = `${u.scene}\u0000${u.spriteId}\u0000${u.state}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Spec 035 D-4: how an expression's spritesheet plays, everywhere it is used:
 * `playback` is written to every sprite state that uses the slot (matched by
 * `pictureSlug`, as `fillPictureSlot` writes `mediaId`), and to no other. A
 * Pictures-view control; the assistant edits playback per sprite through
 * `editSprite` and is not offered this (`STORY_AGENT_EXCLUDED`).
 */
export const setPicturePlayback: StoryOperation<SetPicturePlaybackArgs> = {
  name: 'setPicturePlayback',
  description: 'Set how a character expression\'s spritesheet plays (speed, loop, once, still) in every place the story uses it.',
  inputSchema: obj({
    slotId: SLOT_ID_SCHEMA,
    playback: PLAYBACK_ARG_SCHEMA,
  }, ['slotId', 'playback']),
  apply(doc, args) {
    const playback = playbackFromArg(args.playback);
    const map = syncPictureMap(doc.pictureMap, doc.scenes);
    const found = map.slots.find((s) => s.id === args.slotId);
    if (!found) return fail(e('not_found', `no picture ${args.slotId} in the picture map`, 'slotId'));
    if (found.kind !== 'character') return fail(e('invalid_args', 'only a character\'s expression plays a spritesheet', 'slotId'));
    const uses = slotStateUses(found);
    if (uses.length === 0) return fail(e('not_found', `no scene uses ${pictureSlotLabel(found)}`, 'slotId'));
    let places = 0;
    const refused: { error?: StoryOperationError } = {};
    const scenes = doc.scenes.map((scene): StoryDocScene => {
      const mine = uses.filter((u) => u.scene === scene.key);
      if (mine.length === 0 || refused.error) return scene;
      const sprites = (scene.doc.sprites ?? []).map((sprite): Sprite => {
        const names = mine.filter((u) => u.spriteId === sprite.id).map((u) => u.state!);
        if (names.length === 0 || refused.error) return sprite;
        for (const name of names) {
          const reason = validateStatePlayback(playback);
          if (reason) {
            refused.error = e('out_of_range', `${reason} (${sprite.name} in ${scene.doc.name || scene.name})`, 'playback');
            return sprite;
          }
        }
        return {
          ...sprite,
          states: sprite.states.map((st) => {
            if (!names.includes(st.name)) return st;
            places += 1;
            return { ...st, playback };
          }),
        };
      });
      return { ...scene, doc: { ...scene.doc, sprites } };
    });
    if (refused.error) return fail(refused.error);
    return {
      ok: true,
      doc: { ...doc, scenes } as StoryDocument,
      summary: `Playback of ${pictureSlotLabel(found)} set in ${places} place${places === 1 ? '' : 's'}`,
      createdIds: [],
    };
  },
};

// ─── 8. setPictureKind ──────────────────────────────────────────────────────

export interface SetPictureKindArgs { subject: string; kind: SpritePictureKind }

/**
 * A sprite subject's kind (026 D-26, D-28), for all its pictures at once:
 * the subject's implicit sprite slots are stored first (empty brief and
 * why, the picture they show), then every stored sprite slot of the subject
 * is re-keyed to the new kind at its position, keeping its notes and
 * picture. Scene content is not touched. The Pictures panel's Kind switch
 * applies it; the story assistant sets a kind through `notePictureSlot`
 * (`STORY_AGENT_EXCLUDED`).
 */
export const setPictureKind: StoryOperation<SetPictureKindArgs> = {
  name: 'setPictureKind',
  description: 'Set what kind of picture a sprite shows, for all its pictures at once: character (a person, cut out, with expressions), prop (an object, cut out, with states) or other (a whole picture with its own background).',
  inputSchema: obj({
    subject: str('The sprite\'s exact name.'),
    kind: { type: 'string', enum: SPRITE_PICTURE_KINDS, description: 'character, prop or other.' },
  }, ['subject', 'kind']),
  apply(doc, args, ctx) {
    if (!isSpritePictureKind(args.kind)) return fail(e('invalid_args', 'kind is character, prop or other', 'kind'));
    const subject = String(args.subject ?? '').trim();
    if (!pictureSlug(subject)) return fail(e('out_of_range', 'subject needs at least one letter or digit', 'subject'));
    const named = subjectSprites(doc, subject);
    if (named.length === 0) return fail(noSpriteError(doc, 'prop', subject, nounOf(ctx)));
    const key = pictureSlug(subject);
    const map = syncPictureMap(doc.pictureMap, doc.scenes);
    const mine = map.slots.filter((sl) => isSpritePictureKind(sl.kind) && pictureSlug(sl.subject) === key);
    const name = named[0].name;
    if (mine.every((sl) => sl.kind === args.kind)) {
      return fail(e('invalid_args', `${name} is already ${pictureKindPhrase(args.kind)}`, 'kind'));
    }
    const stored = doc.pictureMap.slots;
    const storedIds = new Set(stored.map((sl) => sl.id));
    const added = mine.filter((sl) => !storedIds.has(sl.id)).map((sl) => storedSlotOf({ ...sl, brief: '', why: '' }));
    const slots = rekeySubject([...stored, ...added], subject, args.kind);
    return {
      ok: true,
      doc: { ...doc, pictureMap: { ...doc.pictureMap, slots } },
      summary: `${name} is now ${pictureKindPhrase(args.kind)}`,
      createdIds: [],
    };
  },
};

export const PICTURE_OPERATIONS = [
  notePictureSlot, removePictureSlot, fillPictureSlot, setPictureFraming, clearPictures, resetPictureSlot, setPicturePlayback, setPictureKind,
] as const;
